<template>
    <div class="report-container">
        <table class="styled-table">
            <!-- Header Section 1: Trip Info -->
            <thead>
                <tr>
                    <th colspan="6" class="label-col">Departure week:</th>
                    <td v-for="(container, index) in containers" :key="'dw-' + index" class="data-col">
                        {{ container.departureWeek }}
                    </td>
                    <th class="total-col" rowspan="7">
                        <div class="total-box-header">N° Containers</div>
                        <div class="total-box-value">{{ containers.length }}</div>
                    </th>
                </tr>
                <tr>
                    <th colspan="6" class="label-col">Arrival WK:</th>
                    <td v-for="(container, index) in containers" :key="'aw-' + index" class="data-col">
                        {{ container.arrivalWeek }}
                    </td>
                </tr>
                <tr>
                    <th colspan="6" class="label-col">Shipping line:</th>
                    <td v-for="(container, index) in containers" :key="'sl-' + index" class="data-col">
                        {{ container.shippingLine }}
                    </td>
                </tr>
                <tr>
                    <th colspan="6" class="label-col">Vessel:</th>
                    <td v-for="(container, index) in containers" :key="'v-' + index" class="data-col">
                        {{ container.vessel }}
                    </td>
                </tr>
                 <tr>
                    <th colspan="6" class="label-col">Container:</th>
                    <td v-for="(container, index) in containers" :key="'c-' + index" class="data-col font-weight-bold">
                        {{ container.container }}
                    </td>
                </tr>
                <tr>
                    <th colspan="6" class="label-col">ETD:</th>
                    <td v-for="(container, index) in containers" :key="'etd-' + index" class="data-col">
                        {{ formatDate(container.etd) }}
                    </td>
                </tr>
                <tr>
                    <th colspan="6" class="label-col">ETA:</th>
                    <td v-for="(container, index) in containers" :key="'eta-' + index" class="data-col">
                        <span v-if="container.isEtaFromExcel" class="excel-highlight-green">
                            {{ formatDate(container.eta) }}
                        </span>
                        <span v-else>{{ formatDate(container.eta) }}</span>
                    </td>
                </tr>
                <tr>
                    <th colspan="6" class="label-col">RTA:</th>
                    <td v-for="(container, index) in containers" :key="'rta-' + index" class="data-col">
                        <span v-if="container.isRtaFromExcel" class="excel-highlight-green">
                            {{ formatDate(container.rta) }}
                        </span>
                        <span v-else>{{ formatDate(container.rta) }}</span>
                        <div v-if="container.dayRTA" style="font-size: 11px; color: #555; font-style: italic;" class="text-lowercase">
                            {{ container.dayRTA }}
                        </div>
                    </td>
                </tr>
                <tr v-if="containers.some(c => c.freightText || c.freight)">
                    <th colspan="6" class="label-col">Freight:</th>
                    <td v-for="(container, index) in containers" :key="'fr-' + index" class="data-col font-weight-bold">
                        {{ container.freightText || container.freight || '-' }}
                    </td>
                </tr>

                <!-- Spacer Row -->
                <tr class="spacer-row"><td :colspan="7 + containers.length"></td></tr>

                <!-- Header Section 2: Data Columns -->
                <tr class="header-row">
                    <th class="header-cell">Sizes</th>
                    <th class="header-cell">FPB</th>
                    <th class="header-cell">Kg</th>
                    <th class="header-cell">Location</th>
                    <th class="header-cell">Type of crop</th>
                    <th class="header-cell">Variety</th>
                    <th v-for="(container, index) in containers" :key="'h-' + index" class="header-cell">
                        PL {{ container.pl ? container.pl : '' }}
                    </th>
                    <th class="header-cell total-header">Total Pallet</th>
                </tr>
            </thead>
            
            <!-- Body: Data Rows -->
            <tbody>
                <tr v-for="(row, index) in flatRows" :key="'row-' + index" :class="{'group-separator': row.isSeparator}">
                    
                    <!-- Separator Row -->
                    <td v-if="row.isSeparator" :colspan="7 + containers.length"></td>

                    <!-- Data Row -->
                    <template v-else>
                        <td class="data-cell text-center">{{ row.sizes }}</td>
                        <td class="data-cell text-center">{{ row.fpb ? row.fpb : '-' }}</td>
                        <td class="data-cell text-right">{{ row.kg ? formatNumber(row.kg) : '-' }}</td>
                        <td class="data-cell">{{ row.location }}</td>
                        <td class="data-cell">{{ row.typeOfCrop }}</td>
                        <td class="data-cell">{{ row.variety }}</td>
                        
                        <!-- Per Container Data -->
                        <td v-for="(container, cIndex) in containers" :key="'d-' + index + '-' + cIndex" class="data-cell text-right">
                                {{ row.containerData[container.container] ? formatNumber(row.containerData[container.container].pl) : '' }}
                        </td>

                        <!-- Row Total -->
                        <td class="data-cell text-right font-weight-bold">
                            {{ formatNumber(row.totalPallet) }}
                        </td>
                    </template>
                </tr>
            </tbody>

            <!-- Footer: Totals -->
            <tfoot>
                <tr class="footer-row">
                    <th colspan="6" class="text-center font-weight-bold">Total</th>
                    <td v-for="(container, index) in containers" :key="'ft-' + index" class="text-right font-weight-bold footer-cell">
                        {{ getContainerTotal(container.container) }}
                    </td>
                    <td class="text-right font-weight-bold footer-cell total-bg">
                        {{ formatNumber(grandTotal) }}
                    </td>
                </tr>
            </tfoot>
        </table>
    </div>
</template>

<script>
export default {
    name: 'DeliveryContainerReport',
    props: {
        reportData: {
            type: Object,
            required: true,
            default: () => ({
                containers: [],
                detail: [],
                totalByContainer: [],
                grandTotal: 0
            })
        }
    },
    computed: {
        containers() {
            console.log("this.reportData.containers", this.reportData);
            return this.reportData.containers || [];
        },
        grandTotal() {
            console.log("this.reportData.grandTotal", this.reportData.grandTotal);
            return this.reportData.grandTotal || 0;
        },
        flatRows() {
            const detail = this.reportData.detail || [];
            if (detail.length === 0) return [];

            const rowMap = new Map();

            detail.forEach(item => {
                const fpbVal = item.fpb !== undefined && item.fpb !== null ? item.fpb : (item.FPB !== undefined && item.FPB !== null ? item.FPB : '');
                const kgVal = item.kg !== undefined && item.kg !== null ? item.kg : (item.Kg !== undefined && item.Kg !== null ? item.Kg : '');
                const key = `${item.sizes}|${fpbVal}|${kgVal}|${item.location}|${item.typeOfCrop}|${item.variety}`;
                
                if (!rowMap.has(key)) {
                    rowMap.set(key, {
                        sizes: item.sizes,
                        fpb: fpbVal,
                        kg: kgVal,
                        location: item.location,
                        typeOfCrop: item.typeOfCrop,
                        variety: item.variety,
                        containerData: {}, 
                        totalPallet: 0,
                        isSeparator: false
                    });
                }

                const row = rowMap.get(key);
                if (item.container) {
                    row.containerData[item.container] = item;
                }
                row.totalPallet += (item.pl || 0); 
            });

             const rows = Array.from(rowMap.values());
            
             // Let's create a separator row object
             const separator = { isSeparator: true };
             
             // Return rows + separator (matches previous logic of 1 group + 1 separator)
             return [...rows, separator];
        }
    },
    methods: {
        formatDate(dateString) {
             if (!dateString) return '';
             // Try to parse basic formats
             const date = new Date(dateString);
             if (isNaN(date.getTime())) return dateString;
             return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        formatNumber(value) {
            if (value === undefined || value === null || value === 0) return '-';
            return Number(value).toFixed(2);
        },
        getContainerTotal(containerName) {
            const totals = this.reportData.totalByContainer || [];
            const found = totals.find(t => t.container === containerName);
            return found ? this.formatNumber(found.total) : '-';
        }
    }
}
</script>

<style scoped>
.report-container {
    overflow-x: auto;
    width: 100%;
    font-family: Arial, sans-serif;
    font-size: 12px;
}

.styled-table {
    border-collapse: collapse;
    width: 100%;
    min-width: 800px;
}

.styled-table th, .styled-table td {
    border: 1px solid #c0c0c0;
    padding: 4px 8px;
}

/* Header Section 1 */
.label-col {
    text-align: left;
    background-color: #ffffff;
    font-weight: bold;
    width: 150px;
    border: none;
}
.data-col {
    text-align: center;
    min-width: 120px;
}
.total-col {
    vertical-align: middle;
    text-align: center;
    background-color: #ffc107; /* Amber/Yellow roughly matching image */
    border: 2px solid #000;
    width: 120px;
}
.total-box-header {
    font-weight: bold;
    border-bottom: 1px solid #000;
    padding-bottom: 5px;
    margin-bottom: 5px;
}
.total-box-value {
    font-size: 1.5em;
    font-weight: bold;
}

/* Spacers */
.spacer-row td {
    border: none;
    height: 15px;
}

/* Header Section 2 */
.header-row th {
    background-color: #ffc107;
    color: #000;
    font-weight: bold;
    text-align: center;
}
.total-header {
    background-color: #ffc107;
}

/* Body */
.data-cell {
    text-align: center;
    color: #000;
}
.text-right {
    text-align: right;
}
.group-separator td {
    border: none;
    height: 10px;
}

/* Footer */
.footer-row td, .footer-row th {
    background-color: #ffc107;
    color: #000;
}
.footer-cell {
    border-top: 2px solid #000;
}
.total-bg {
     background-color: #ffb300;
}

.excel-highlight-green {
    background-color: #d4edda;
    color: #155724;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: bold;
    border: 1px solid #c3e6cb;
    display: inline-block;
}

</style>
